import { useCallback, useMemo } from 'react'
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  type NodeTypes,
  type OnSelectionChangeFunc,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { useEditorStore } from '@/store'
import DataNode from './DataNode'

const nodeTypes: NodeTypes = {
  dataNode: DataNode,
}

const typeColors: Record<string, string> = {
  string: '#22c55e',
  number: '#3b82f6',
  boolean: '#f59e0b',
  null: '#6b7280',
  object: '#8b5cf6',
  array: '#ec4899',
  root: '#475569',
}

export default function GraphView() {
  const { t } = useTranslation()
  const { 
    nodes, 
    edges, 
    metadata,
    showMinimap, 
    showControls,
    setSelectedNode,
    theme,
  } = useEditorStore()

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState(nodes)
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState(edges)

  // Sync store nodes with flow nodes
  useMemo(() => {
    setFlowNodes(nodes)
    setFlowEdges(edges)
  }, [nodes, edges, setFlowNodes, setFlowEdges])

  const handleSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes }) => {
      const selected = nodes[0]
      setSelectedNode(selected?.id || null)
    },
    [setSelectedNode]
  )

  const minimapNodeColor = useCallback((node: unknown) => {
    const nodeData = node as { data: { type: string } }
    return typeColors[nodeData.data.type] || '#6b7280'
  }, [])

  const isDark = theme === 'dark' || 
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  if (nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-slate-900/50">
        <div className="text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-slate-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" 
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('errors.empty')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
            Paste or type your JSON, YAML, XML, or TOML in the editor to visualize it
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full w-full relative">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onSelectionChange={handleSelectionChange}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={isDark ? '#334155' : '#e2e8f0'}
        />
        
        {showControls && (
          <Controls
            showZoom
            showFitView
            showInteractive
            position="bottom-left"
          />
        )}
        
        {showMinimap && (
          <MiniMap
            nodeColor={minimapNodeColor}
            maskColor={isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
            position="bottom-right"
            pannable
            zoomable
          />
        )}
      </ReactFlow>

      {/* Stats overlay */}
      {metadata && (
        <div className="absolute top-4 right-4 px-3 py-2 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm border text-xs">
          <div className="flex items-center gap-4 text-slate-500 dark:text-slate-400">
            <span>
              <strong className="text-slate-700 dark:text-slate-300">{metadata.nodeCount}</strong> nodes
            </span>
            <span>
              <strong className="text-slate-700 dark:text-slate-300">{metadata.maxDepth}</strong> depth
            </span>
            <span>
              <strong className="text-slate-700 dark:text-slate-300">{metadata.parseTime.toFixed(1)}</strong>ms
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
